/**
 * HUD — DOM overlay above the WebGL canvas.
 *
 * Milestone-1.5 scaffold: ships the elements and state-update API the rest
 * of the client will write into. Real orbital readouts (apoapsis/periapsis),
 * SoI badge, RWR pane, and event feed land in milestones 6 and 10. This
 * file deliberately keeps to fuel + connection + a placeholder readout so
 * we can wire the responsive CSS and confirm reflow without coupling to
 * the simulator.
 *
 * Routing of user actions through the codebase:
 *
 *   simulator tick           -> hud.setFuel(0..1)
 *   net/Reconnector events   -> hud.setConnectionState('connected' | ...)
 *   simulator dominant body  -> hud.setNearBody(name)
 */

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "offline";

export class HUD {
	readonly root: HTMLElement;
	private readonly fuelFill: HTMLElement;
	private readonly connection: HTMLElement;
	private readonly nearBody: HTMLElement;

	constructor(parent: HTMLElement) {
		this.root = document.createElement("div");
		this.root.className = "hud";
		this.root.innerHTML = `
			<div class="hud__top">
				<div class="hud__panel hud__connection" data-conn data-state="connecting">connecting…</div>
				<div class="hud__panel hud__room">squid // milestone 1.5</div>
			</div>
			<div class="hud__bottom-left">
				<div class="hud__panel hud__fuel">
					<div>fuel</div>
					<div class="hud__fuel-bar"><span class="hud__fuel-fill" data-fuel></span></div>
				</div>
			</div>
			<div class="hud__bottom-right">
				<div class="hud__panel" data-near>near: —</div>
			</div>
		`;
		parent.appendChild(this.root);

		this.fuelFill = this.root.querySelector<HTMLElement>("[data-fuel]")!;
		this.connection = this.root.querySelector<HTMLElement>("[data-conn]")!;
		this.nearBody = this.root.querySelector<HTMLElement>("[data-near]")!;
	}

	setFuel(unit: number): void {
		const pct = Math.max(0, Math.min(1, unit)) * 100;
		this.fuelFill.style.width = `${pct.toFixed(1)}%`;
	}

	setConnectionState(state: ConnectionState): void {
		this.connection.dataset.state = state;
		this.connection.textContent = stateLabel(state);
	}

	setNearBody(name: string | null): void {
		this.nearBody.textContent = name ? `near: ${name}` : "near: —";
	}

	dispose(): void {
		this.root.remove();
	}
}

function stateLabel(state: ConnectionState): string {
	switch (state) {
		case "connecting":
			return "connecting…";
		case "connected":
			return "online";
		case "reconnecting":
			return "reconnecting…";
		case "offline":
			return "offline";
	}
}
